/**
 * permissions-store.ts — WAYP-264 / WAYP-267
 *
 * Client-side mirror of the server permissions catalogue.
 * Keep in sync with packages/server/src/core/auth/permissions.ts
 *
 * WAYP-264: Expanded to cover ALL V2 modules:
 *   customers, gps, payments, expenses, brokerage, escrow, loans, usps
 */

import { create } from 'zustand';

/* ------------------------------------------------------------------ */
/*  Permission types (mirrored from server permissions.ts)             */
/* ------------------------------------------------------------------ */

export const PERMISSIONS = {
  // ── Core operations ────────────────────────────────────────────────
  // WAYP-945 — extended from 4 to 7 actions to mirror the server catalogue
  // (loads now ships export / restore / import per Brief §5.1).
  loads:         { view: true, create: true, edit: true, delete: true, export: true, restore: true, import: true },
  dispatch:      { view: true, manage: true, auto_schedule: true },
  drivers:       { view: true, create: true, edit: true, delete: true },
  carriers:      { view: true, create: true, edit: true, delete: true },
  // WAYP-611: Supplies module — vendor master, sibling to Carriers.
  // Mirrors server permissions.ts (WAYP-607). Same 4 actions as Carriers.
  supplies:      { view: true, create: true, edit: true, delete: true },
  // WAYP-513: `restore` mirrors server permissions.ts for the soft-delete
  // restore endpoint gate.
  // WAYP-CUST-06 (Wave 1): `view-financial` is the credit-history /
  // financial-data gate; server adds it under module `customers` with the
  // hyphenated action key. Mirrored here so `usePermissionsStore.can(
  // 'customers', 'view-financial')` returns the live grant value rather
  // than `false` by fall-through.
  customers:     {
    view: true,
    create: true,
    edit: true,
    delete: true,
    restore: true,
    'view-financial': true,
  },
  // WAYP-EQUIP-10: `import` + `export` + `restore` mirror server-side
  // equipment permission actions (see server/permissions.ts). The grid
  // already gates on `equipment.export` / `equipment.restore` (EQUIP-06/08);
  // `equipment.import` is required by the new Import Modal trigger.
  equipment:     { view: true, create: true, edit: true, delete: true, import: true, export: true, restore: true },

  // ── Finance ────────────────────────────────────────────────────────
  // #1077 — Invoices V2 parity (server `invoices` module). Renamed from
  // `invoicing`; legacy `send` action dropped. Six granular actions match
  // the server catalogue and Locations precedent:
  //   view / create / edit / delete / export / restore
  invoices:      { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  settlements:   { view: true, create: true, approve: true },
  payments:      { view: true, create: true, edit: true },
  // WAYP-392: Expenses module (V1 parity port) — granular actions including
  // export (CSV) and restore (soft-delete recovery) per umbrella AC.
  expenses:      { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  brokerage:     { view: true, manage: true },
  // #920 — Escrow Accounts V2 V1-parity rebuild. Six granular actions
  // (view/create/edit/delete/export/restore) replace the legacy
  // `escrow: { view, manage }` shape. Sidebar `value` field matches:
  // `escrow_accounts`. Admin + Super Admin auto-grant on the server via
  // `allPermissions(true)`. Audit-log module display name registered
  // as `'Escrow Accounts'` at the route layer.
  escrow_accounts: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // #898 — V1 parity: 6 granular actions replace the legacy `manage` action.
  // Admin + Super Admin auto-grant on the server via `allPermissions(true)`.
  // ATL-001129 (SEC-020): `write_off` is a distinct action from `edit` —
  // it gates the dedicated write-off endpoint (which erases the linked
  // DriverDeduction recovery schedule) and requires an audit reason.
  loans:         { view: true, create: true, edit: true, delete: true, export: true, restore: true, write_off: true },

  // ── Reporting & data ───────────────────────────────────────────────
  reports:       { view: true, export: true },
  edi:           { view: true, process: true },

  // ── Field ops ─────────────────────────────────────────────────────
  fuel:          { view: true },
  maps:          { view: true },
  gps:           { view: true, manage: true },
  maintenance:   { view: true, create: true, edit: true },
  // WAYP-506: granular USPS actions mirror backend permissions (see
  // packages/server/src/core/auth/permissions.ts). `manage` retained for
  // backward-compat with callers that still check the legacy action.
  usps:          { view: true, create: true, edit: true, delete: true, export: true, restore: true, manage: true },
  cross_dock:    { view: true, manage: true },

  // ── Settings / Types ──────────────────────────────────────────────
  deduction_types: { view: true, create: true, edit: true, delete: true, export: true },
  time_off_types:  { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  equipment_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  expense_types:   { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  department_types:            { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  custom_templates:            { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  carrier_requirement_types:   { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  driver_rating_questions:     { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  driver_expiry_types:         { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  taxes: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  trading_partners: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  fuel_rates: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-361: Equipment Expiry Types
  equipment_expiry_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-366: Terminals
  terminals: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // ATL-001050 — the standalone `override` action (WAYP-408, Load
  // file-checklist completion override) was retired: bypassing the
  // required-documents gate now follows `loads.edit` / `customers.edit`
  // directly instead of a separate permission.
  file_checklist: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  routing_row_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-368: Work Order Types
  work_order_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-369: Purchase Order Types
  purchase_order_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-386 / ATL-001047: Purchase Orders module — mirrors server permissions.ts.
  // `approve` removed (approval follows `edit`). `notify` default OFF for
  // every role; admins must opt users in via the user-permissions popup.
  purchase_orders: { view: true, create: true, edit: true, delete: true, export: true, restore: true, notify: true },
  load_categories: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  commodity_categories: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-494.3: Load Statuses
  load_statuses: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-45: Custom Dispatch Tabs — org-wide DB-backed dispatch tabs + per-module
  // Dispatch Tab Settings sub-form. 6 actions per the locked decision.md #5.
  custom_dispatch_tabs: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-500: Custom Data Types
  custom_data_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-370: Rate/Service Types
  rate_service_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // Weight-Based Zone Rating: Zones master (gated by the weight_based_zone_rating feature).
  zones: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-502: Other Charge / Reimbursement Types
  other_charge_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // #1087: Custom Charges / Credits — Finance menu V1→V2 foundation.
  // Four actions per ticket spec (no export/restore/import in phase 1).
  custom_charges: { view: true, create: true, edit: true, delete: true },
  // WAYP-501: Slips
  slips: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-561.3: Locations — V1 parity module (7 actions incl. Import).
  locations: { view: true, create: true, edit: true, delete: true, export: true, restore: true, import: true },
  // WAYP-814: Quotes — Operations module (V1 parity port). Six actions per
  // umbrella AC (View / Create / Edit / Delete / Export / Restore). Mirrors
  // server permissions.ts. Module visibility in the sidebar is also
  // conditional on the `enableQuotes` Company Preference.
  quotes: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // #670: Work Orders — V1 parity from Equipment, lives under Finance nav in V2.
  // Six actions per umbrella AC. Mirrors the server catalogue (no `import`).
  work_orders: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-599: Holidays — V1 parity module (7 actions incl. Import; Import has no UI button per PO direction).
  holidays: { view: true, create: true, edit: true, delete: true, export: true, restore: true, import: true },
  // WAYP-680: Frequency Types — V1 parity module (umbrella #677).
  // 6 actions per umbrella AC. `import` is deliberately omitted (locked PO decision).
  frequency_types: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // USPS Contracts V2 (umbrella #676, slice 02): mirror server `contracts`
  // scope. Six granular actions. `export` + `restore` keys are seeded so
  // the Settings → Users permissions grid renders the full row, even
  // though the routes that consume them ship in slices 03 / 08.
  contracts: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // USPS Trips V2 (umbrella #764, slice 02): mirror server `trips`
  // scope. Six granular actions matching the `contracts` shape.
  // `export` + `restore` keys are seeded so the Settings → Users
  // permissions grid renders the full row, even though the routes
  // that consume them ship in slice 03.
  trips: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // WAYP-927: Driver Routes module (V1 /bids/ → V2 /driver-routes/ port).
  // Six granular actions matching the `trips` shape. Default Admin +
  // Super Admin enabled (server-side auto-grant); every other role denies
  // until explicitly granted via the Settings → Users permissions grid.
  driver_routes: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // USPS Recurring Loads V3 (umbrella #966, slice b/i — #968): mirror server
  // `recurring` scope. Six granular actions matching the `contracts` /
  // `trips` shape. `export` + `restore` keys are seeded so the Settings →
  // Users permissions grid renders the full row, even though the routes
  // that consume them ship in later slices (d/e).
  recurring: { view: true, create: true, edit: true, delete: true, export: true, restore: true },
  // USPS Carrier Loads V2 (umbrella #1141, slice #1142): mirror server
  // `usps_loads` scope. Six granular actions matching the `contracts` /
  // `trips` / `recurring` shape. `export` + `restore` keys are seeded
  // so the Settings → Users permissions grid renders the full row, even
  // though the routes that consume them ship in later slices. Default
  // Admin + Super Admin enabled (server-side auto-grant); every other
  // role denies until explicitly granted via the Settings → Users grid.
  // Audit-log module display name registered server-side as `'USPS Load'`
  // (singular, matches the `'USPS Contract'` precedent).
  // `view-financial` (2026-08-06) — V1's `permission_usps_financials`. V1 gated
  // the load form's Margin/profit step and the rate rows behind it
  // (`form.blade.php`), so cost + profit were not visible to every dispatcher.
  // V2 had no equivalent, leaving the Margin tab open to all. Hyphenated action
  // key follows the `customers: 'view-financial'` precedent. Admin +
  // super_admin auto-grant via `allPermissions(true)` server-side; every other role denies
  // until explicitly granted, which is the safe direction for a financial gate.
  usps_loads: { view: true, create: true, edit: true, delete: true, export: true, restore: true, 'view-financial': true },
  // USPS 5500 (V1 `/invoicing/usps-5500`) — mirror server `usps_5500` scope.
  // view/edit/delete/export only (no create). Default Admin + Super Admin
  // (server auto-grant); every other role denies until granted.
  usps_5500: { view: true, edit: true, delete: true, export: true },
  // USPS Renewal (#1922) — mirror server `usps_renewals` scope. Four actions:
  // view/create/approve/delete. Default Admin + Super Admin (server auto-grant);
  // every other role denies until granted.
  usps_renewals: { view: true, create: true, approve: true, delete: true },
  // Reconcile Slips (#1564) — mirror server `reconcile_slips` scope. Five
  // actions: view/edit/delete/export/approve. Default Admin + Super Admin
  // (server auto-grant); every other role denies until granted.
  reconcile_slips: { view: true, edit: true, delete: true, export: true, approve: true },
  // CLEAR Rates (#1652) — mirror server `clear_rates` scope. view/import/export.
  // Default Admin + Super Admin (server auto-grant); every other role denies
  // until granted. Page gated additionally on the `organization.usps500` pref.
  clear_rates: { view: true, import: true, export: true },
  // #1763 — USPS Voucher Import — mirror server `usps_vouchers` scope.
  // view/import/export. Default Admin + Super Admin (server auto-grant); every
  // other role denies until granted. Page gated additionally on `usps500` pref.
  usps_vouchers: { view: true, import: true, export: true },
  // FourKites weekly USPS compliance scores — mirror the server scopes.
  //   • `fourkites_compliance` (carrier surface): view/export their own scores.
  //   • `fourkites_compliance_admin` (internal surface): upload/manage mapping —
  //     every admin route is ALSO super_admin-gated on the server.
  fourkites_compliance: { view: true, export: true },
  fourkites_compliance_admin: { view: true, import: true, export: true, manage: true },
  // #1770 — USPS Reconciliation — mirror server `usps_reconciliation` scope.
  // view/reconcile/export. Default Admin + Super Admin (server auto-grant).
  usps_reconciliation: { view: true, reconcile: true, export: true },
  // USPS Tenders V2 — quotes-based tender queue. Mirrors server `usps_tenders`.
  usps_tenders: { view: true, export: true },
  // Process EDI (#1755) — USPS EDI exception-review console. `view` gates the
  // page; `edit` gates the per-exception load-update actions (Add To Load /
  // Remove From Loads / Update Load / Update NASS Code / …); `reject` gates the
  // Reject Trip / Reject Time Change actions. Mirrors the server
  // `usps_process_edi` scope (View / Edit / Reject per the #1755 spec). Default
  // Admin + Super Admin; every other role denies until granted via Settings →
  // Users. (The per-tab "Export to Excel" gates on `view`.)
  usps_process_edi: { view: true, edit: true, reject: true },
  // WAYP-395: Customs Brokers — V2 parity port (Phase E.1). Mirrors the
  // server `customs_brokers` permission catalogue: 7 granular actions
  // including `import` for the bulk-CSV wizard. Page is gated by the
  // `mod_customs_broker` enterprise feature, the operational
  // `enable_custom_broker_info` toggle, and `companyType ∈ {broker, mixed}`
  // at the route + render layer.
  customs_brokers: { view: true, create: true, edit: true, delete: true, export: true, restore: true, import: true },
  // WAYP-952: Rating sub-page modules (V1 parity). Mirrors server permissions.ts.
  // `approve` gates the V1 "Update Rate(s) in Rating Table" Load-form action
  // (pushing a rate edited on a Load back into the rating tables); independent
  // of the page CRUD actions.
  // `delete` + `restore` omitted: the Rating pivot tables have no per-row
  // delete / show-deleted UI, so those actions are not user-grantable here.
  billing_rates: { view: true, create: true, edit: true, export: true, approve: true },
  fsc_rates: { view: true, create: true, edit: true, export: true, approve: true },
  accessorial_rates: { view: true, create: true, edit: true, export: true, approve: true },

  // ── Platform ──────────────────────────────────────────────────────
  settings:      { view: true, manage: true },
  users:         { view: true, manage: true },
  support:       { view: true, manage: true },
  documents:     { view: true, create: true, edit: true, delete: true },
  integrations:  { view: true, manage: true },
  import_export: { view: true, import: true, export: true },
  ai:            { view: true, manage: true },
  // ATL-001415: AI Email Intake (mirror of the server module).
  email_intake:  { view: true, review: true, configure: true },
  notifications: { view: true, manage: true },
  // Blacklist Carriers (V1 parity) — mirrors server permissions.ts.
  //   maintain  ← V1 permission_blacklist_carrier (gates the Blacklist checkbox
  //              on the Driver/Carrier form + manual endpoints)
  // ATL-001042: the warn-notification opt-in is NO LONGER a permission — it
  // moved to the per-user `notifyBlacklistRatingWarning` toggle in the
  // Notification Preferences modal (so it works for driver/carrier, whose
  // permission-matrix gear is hidden). The rating engine resolves recipients
  // from that flag.
  blacklist_carriers: { maintain: true },
  // Commission module — manages LoadCategoryCommissionRule rows and views
  // UserCommissionSummary reports. Mirrors server permissions.ts.
  commissions: { view: true, create: true, edit: true, delete: true },
} as const;

export type PermissionModule = keyof typeof PERMISSIONS;
export type UserPermissions = {
  [M in PermissionModule]?: {
    [A in keyof (typeof PERMISSIONS)[M]]?: boolean;
  };
};

export const ALL_ACTIONS = [
  'view', 'create', 'edit', 'delete', 'manage',
  'auto_schedule', 'send', 'approve', 'export', 'import', 'process', 'restore',
  // WAYP-386: Purchase Orders — `notify` recipient-list filter for the
  // on-create email notification (introduced by WAYP-387).
  'notify',
  // ATL-001415: AI Email Intake.
  'review', 'configure',
] as const;

export const MODULE_LIST: PermissionModule[] = Object.keys(PERMISSIONS) as PermissionModule[];

export function actionsForModule(mod: PermissionModule): string[] {
  return Object.keys(PERMISSIONS[mod]);
}

/** Human-friendly labels */
export const MODULE_LABELS: Record<PermissionModule, string> = {
  loads: 'Loads',
  dispatch: 'Dispatch',
  drivers: 'Drivers',
  carriers: 'Carriers',
  // WAYP-611: Supplies module — sibling to Carriers (vendor master).
  // Label renamed from "Supplies" to "Suppliers" per WAYP-684 (2026-04-29
  // product decision). Code key `supplies` is unchanged.
  supplies: 'Suppliers',
  customers: 'Customers',
  equipment: 'Equipment',
  // #1077 — module key renamed from `invoicing` → `invoices`; label
  // updated to "Invoices" (plural, matches the route + sidebar entry).
  invoices: 'Invoices',
  settlements: 'Settlements',
  payments: 'Payments',
  expenses: 'Expenses',
  brokerage: 'Brokerage',
  // #920 — V1 parity rebuild renamed the module key to `escrow_accounts`.
  escrow_accounts: 'Escrow Accounts',
  loans: 'Loans',
  reports: 'Reports',
  fuel: 'Fuel',
  maps: 'Maps',
  gps: 'GPS',
  edi: 'EDI',
  maintenance: 'Maintenance',
  usps: 'USPS',
  cross_dock: 'Cross-Dock',
  deduction_types:         'Deduction / Reimbursement Types',
  time_off_types:          'Time Off Types',
  equipment_types:         'Equipment Types',
  expense_types:           'Expense Types',
  department_types:            'Department Types',
  custom_templates:            'Custom Templates',
  carrier_requirement_types:   'Carrier Requirement Types',
  driver_rating_questions:     'Driver / Carrier Rating',
  driver_expiry_types:         'Driver Expiries',
  taxes: 'Taxes',
  trading_partners: 'Trading Partners',
  fuel_rates: 'Fuel Rates',
  equipment_expiry_types: 'Equipment Expiries',
  terminals: 'Terminals',
  file_checklist: 'File Checklist',
  routing_row_types: 'Routing Row Types',
  work_order_types: 'Work Order Types',
  purchase_order_types: 'Purchase Order Types',
  // WAYP-386: Purchase Orders module
  purchase_orders: 'Purchase Orders',
  load_categories: 'Load Categories',
  commodity_categories: 'Commodity Categories',
  load_statuses: 'Load Statuses',
  // WAYP-45: matches the V1 audit-log display string `Custom Dispatch Tabs`
  custom_dispatch_tabs: 'Custom Dispatch Tabs',
  custom_data_types: 'Custom Data Types',
  rate_service_types: 'Rate Types',
  zones: 'Zones',
  other_charge_types: 'Other Charge / Reimbursement Types',
  // #1087: Custom Charges / Credits — Finance menu V1→V2 foundation.
  custom_charges: 'Custom Charges',
  slips: 'Slips',
  locations: 'Locations',
  // WAYP-814: matches the audit-log module name "Quotes" (plural) per umbrella AC.
  quotes: 'Quotes',
  work_orders: 'Work Orders',
  holidays: 'Holidays',
  frequency_types: 'Frequency Types',
  contracts: 'Contracts',
  trips: 'USPS Trips',
  driver_routes: 'Driver Routes',
  // USPS Recurring Loads V3 (umbrella #966) — matches the audit-log module
  // display name 'USPS Recurring Loads' (plural).
  recurring: 'USPS Recurring Loads',
  // USPS Carrier Loads V2 (umbrella #1141) — label `'USPS Loads'` (plural)
  // for the Settings → Users permissions grid row. Distinct from the
  // backend audit-log module display name `'USPS Load'` (singular), which
  // is what surfaces on the audit-log grid per slice #1142 brief. The
  // permissions-grid label is the user-facing module name; the audit-log
  // module name is the per-entity label — they are intentionally
  // different because the audit log shows per-row entries (singular feel)
  // while the permissions matrix shows per-module rows (plural feel).
  usps_loads: 'USPS Loads',
  usps_5500: 'USPS 5500',
  usps_renewals: 'USPS Renewals',
  usps_tenders: 'USPS Tenders',
  usps_process_edi: 'Process EDI',
  reconcile_slips: 'Reconcile Slips',
  clear_rates: 'CLEAR Rates',
  usps_vouchers: 'USPS Vouchers',
  fourkites_compliance: 'FourKites Compliance',
  fourkites_compliance_admin: 'FourKites Compliance (Admin)',
  usps_reconciliation: 'USPS Reconciliation',
  customs_brokers: 'Customs Brokers',
  // WAYP-952: Rating sub-page modules.
  billing_rates: 'Billing Rates',
  fsc_rates: 'FSC Rates',
  accessorial_rates: 'Accessorial Rates',
  settings: 'Settings',
  users: 'Users',
  support: 'Support',
  documents: 'Documents',
  integrations: 'Integrations',
  import_export: 'Import / Export',
  ai: 'AI Assistant',
  email_intake: 'AI Email Intake',
  notifications: 'Notifications',
  blacklist_carriers: 'Blacklist Carriers',
  commissions: 'Commissions',
};

export const ACTION_LABELS: Record<string, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  manage: 'Manage',
  auto_schedule: 'Auto Schedule',
  send: 'Send',
  approve: 'Approve',
  export: 'Export',
  import: 'Import',
  process: 'Process',
  restore: 'Restore',
  // WAYP-386: Purchase Orders `notify` action.
  notify: 'Notify',
  // WAYP-1246: Loads History sub-permission.
  history: 'History',
  // Blacklist Carriers (V1 parity).
  maintain: 'Maintain Blacklist',
  // Hyphenated module-specific actions (Customers). Labelled so the
  // permissions UI shows a readable name instead of the raw key.
  'view-financial': 'View Financial',
  'approve-reject-portal': 'Approve / Reject Portal',
  // ATL-001415: AI Email Intake.
  review: 'Review',
  configure: 'Configure',
};

/**
 * Short, human-readable descriptions for each permission action, shown beneath
 * the action label in the User Permissions modal. Generic + verb-based: an
 * action means the same thing across modules, so a single map stays consistent
 * and low-maintenance. Falls back to no description for any unmapped action.
 */
export const ACTION_DESCRIPTIONS: Record<string, string> = {
  view: 'See records in this module',
  create: 'Add new records',
  edit: 'Modify existing records',
  delete: 'Delete records',
  manage: 'Full management access',
  auto_schedule: 'Run automatic scheduling',
  send: 'Send records (e.g. by email)',
  approve: 'Approve records',
  export: 'Export or download records',
  import: 'Bulk-import records',
  process: 'Process records',
  restore: 'Restore deleted records',
  notify: 'Receive related notifications',
  history: 'View historical records',
  'view-financial': 'View financial / credit details',
  'approve-reject-portal': 'Approve or reject portal access',
  review: 'Approve or reject AI-proposed records',
  configure: 'Set up connections and rules',
};

/* ------------------------------------------------------------------ */
/*  Store                                                              */
/* ------------------------------------------------------------------ */

interface PermissionsStore {
  /** Resolved permissions for the current user (null = not loaded yet) */
  permissions: UserPermissions | null;
  /** Whether the permissions have been fetched at least once */
  loaded: boolean;

  setPermissions: (perms: UserPermissions) => void;
  clear: () => void;

  /** Check if current user has a specific permission */
  can: (module: PermissionModule, action: string) => boolean;
  /** Check if current user can view a module (shorthand) */
  canView: (module: PermissionModule) => boolean;
}

export const usePermissionsStore = create<PermissionsStore>((set, get) => ({
  permissions: null,
  loaded: false,

  setPermissions: (perms) => set({ permissions: perms, loaded: true }),
  clear: () => set({ permissions: null, loaded: false }),

  can: (module, action) => {
    const { permissions } = get();
    if (!permissions) return true; // not loaded yet — permissive default to avoid flash
    const modPerms = permissions[module] as Record<string, boolean> | undefined;
    return modPerms?.[action] ?? false;
  },

  canView: (module) => {
    return get().can(module, 'view');
  },
}));
